import { useLayoutEffect, useRef } from "react";
import { decodeFrame } from "../decode";
import { prefersReducedMotion } from "../motion";

// Stand-in glyphs change every 45 ms — per-frame flicker reads as noise.
const GLYPH_MS = 45;

/** Text that resolves out of random glyphs on mount (and whenever it changes).
 *  Decorative: the span is aria-hidden, so put the real text on the parent
 *  (e.g. `aria-label`). The frames are written straight into the text node to
 *  keep a 60 fps effect out of React's render path. */
export function DecodeText({ text, durationMs = 420 }: { text: string; durationMs?: number }) {
  const ref = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const node = ref.current?.firstChild;
    if (!node) return;
    // The cleanup of a previous run may have written the old text back.
    node.nodeValue = text;
    if (prefersReducedMotion()) return;
    const length = Array.from(text).length;
    const start = performance.now();
    let raf = 0;
    let lastStep = -1;
    const draw = (now: number) => {
      const elapsed = now - start;
      if (elapsed >= durationMs) {
        node.nodeValue = text;
        return;
      }
      const step = Math.floor(elapsed / GLYPH_MS);
      if (step !== lastStep) {
        lastStep = step;
        node.nodeValue = decodeFrame(text, Math.floor((elapsed / durationMs) * length));
      }
      raf = requestAnimationFrame(draw);
    };
    draw(start);
    return () => {
      cancelAnimationFrame(raf);
      node.nodeValue = text;
    };
  }, [text, durationMs]);

  return (
    <span ref={ref} aria-hidden="true">
      {text}
    </span>
  );
}
